import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './Admin.css';
import { useToast, useConfirm } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';

const ROLES = [
  {
    key: 'admin',
    label: 'Admin',
    color: '#c8a96e',
    description: 'Full access — manage everything including employees',
    permissions: ['create', 'read', 'edit', 'delete', 'manage_employees', 'view_customers'],
  },
  {
    key: 'manager',
    label: 'Manager',
    color: '#2ecc71',
    description: 'Create, read, edit agreements + view customers',
    permissions: ['create', 'read', 'edit', 'view_customers'],
  },
  {
    key: 'sales_agent',
    label: 'Sales Agent',
    color: '#3498db',
    description: 'Create and read agreements only',
    permissions: ['create', 'read'],
  },
  {
    key: 'viewer',
    label: 'Viewer',
    color: '#95a5a6',
    description: 'Read-only access to agreements and customers',
    permissions: ['read', 'view_customers'],
  },
];

const getRoleInfo = (key) => ROLES.find(r => r.key === key) || ROLES[3];

function Admin() {
  const navigate = useNavigate();
  const { currentUser, login, logout } = useAuth();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('agreements'); // agreements | employees | customers
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [custTypeFilter, setCustTypeFilter] = useState('all');
  const [selectedCust, setSelectedCust] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [empView, setEmpView] = useState('list'); // list | add | edit
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empForm, setEmpForm] = useState({ name: '', username: '', password: '', role: 'viewer' });
  const [empError, setEmpError] = useState('');
  const [empSaving, setEmpSaving] = useState(false);
  const { showToast, ToastComponent } = useToast();
  const { showConfirm, ConfirmComponent } = useConfirm();

  const handleLogin = async () => {
    setLoginLoading(true);
    setError('');
    const result = await login(loginData.username, loginData.password);
    if (!result.success) {
      setError('Invalid username or password.');
    }
    setLoginLoading(false);
  };

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('agreements')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) setAgreements(data);
    setLoading(false);
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) showToast('Failed to load employees.', 'error');
    else if (data) setEmployees(data);
    setLoadingEmployees(false);
  }, [showToast]);

  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) showToast('Failed to load customers.', 'error');
    else if (data) setCustomers(data);
    setLoadingCustomers(false);
  }, [showToast]);

  useEffect(() => {
    if (currentUser) {
      fetchAgreements();
      fetchEmployees();
      fetchCustomers();
    }
  }, [currentUser, fetchAgreements, fetchEmployees, fetchCustomers]);

  const filtered = filter === 'all'
    ? agreements
    : agreements.filter(a => a.agreement_type === filter);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const handleSaveEmployee = async () => {
    if (!empForm.name || !empForm.username || !empForm.password) {
      setEmpError('Name, username and password are required.');
      return;
    }
    setEmpSaving(true);
    setEmpError('');

    if (empView === 'add') {
      const { error } = await supabase.from('employees').insert([{
        name: empForm.name,
        username: empForm.username,
        password: empForm.password,
        role: empForm.role,
        is_active: true,
      }]);
      if (error) {
        setEmpError(error.message.includes('unique') ? 'Username already exists.' : 'Error saving employee.');
      } else {
        await fetchEmployees();
        setEmpView('list');
        setEmpForm({ name: '', username: '', password: '', role: 'viewer' });
      }
    } else if (empView === 'edit') {
      const { error } = await supabase.from('employees')
        .update({ name: empForm.name, username: empForm.username, password: empForm.password, role: empForm.role })
        .eq('id', selectedEmp.id);
      if (error) {
        setEmpError('Error updating employee.');
      } else {
        await fetchEmployees();
        setEmpView('list');
        setSelectedEmp(null);
        setEmpForm({ name: '', username: '', password: '', role: 'viewer' });
      }
    }
    setEmpSaving(false);
  };

  const handleToggleActive = async (emp) => {
    const { error } = await supabase.from('employees').update({ is_active: !emp.is_active }).eq('id', emp.id);
    if (error) showToast('Failed to update employee status.', 'error');
    else fetchEmployees();
  };

  const handleDeleteEmployee = (emp) => {
    showConfirm({
      title: 'Delete Employee',
      message: `Are you sure you want to delete "${emp.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await supabase.from('employees').delete().eq('id', emp.id);
        fetchEmployees();
        showToast(`Employee "${emp.name}" deleted.`, 'success');
      },
    });
  };

  const handleEditEmployee = (emp) => {
    setSelectedEmp(emp);
    setEmpForm({ name: emp.name, username: emp.username, password: emp.password, role: emp.role });
    setEmpView('edit');
    setEmpError('');
  };

  // ── LOGIN ──
  if (!currentUser) {
    return (
      <div className="admin-login">
        <div className="login-card">
          <div className="login-icon">🔐</div>
          <p className="login-brand">Al Mushtaraka Trading Co.</p>
          <h2>ADMIN PANEL</h2>
          <p>Enter your credentials to access the dashboard</p>
          <div className="login-input-wrapper">
            <span className="login-input-icon">👤</span>
            <input
              type="text"
              placeholder="Username"
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              autoFocus
            />
          </div>
          <div className="login-input-wrapper">
            <span className="login-input-icon">🔑</span>
            <input
              type="password"
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>
          {error && <p className="login-error">⚠️ {error}</p>}
          <button onClick={handleLogin} disabled={loginLoading}>
            {loginLoading ? 'Checking...' : 'Access Dashboard →'}
          </button>
        </div>
      </div>
    );
  }

  // ── ACCESS DENIED (logged in, but not an admin) ──
  if (currentUser.role !== 'admin') {
    return (
      <div className="admin-login no-navbar">
        <div className="login-card">
          <div className="login-icon">⛔</div>
          <p className="login-brand">Al Mushtaraka Trading Co.</p>
          <h2>ACCESS DENIED</h2>
          <p>Your account does not have permission to view the Admin Panel.</p>
          <button onClick={() => navigate('/hub')}>← Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // ── AGREEMENT DETAIL ──
  if (selected) {
    const detailCur = selected.currency || 'AED';
    const buyers = selected.buyers && selected.buyers.length > 0
      ? selected.buyers
      : [{
          buyerCompany: selected.buyer_company,
          licenseNumber: selected.license_number,
          representativeName: selected.representative_name,
          eid: selected.eid,
          mobile: selected.mobile,
        }];

    return (
      <div className="page">
        <div className="admin-detail-header">
          <button className="btn-back-admin" onClick={() => setSelected(null)}>← Back to All Agreements</button>
          <span className={`badge ${selected.agreement_type}`}>
            {selected.agreement_type === 'sale' ? 'Sale Agreement' : 'Lease Cum Sale'}
          </span>
        </div>
        <div className="detail-card">
          <div className="detail-section">
            <h3>Agreement Info</h3>
            <div className="detail-grid">
              <div><label>Ref Number</label><p>{selected.ref_number}</p></div>
              <div><label>Date</label><p>{selected.date}</p></div>
              <div><label>Type</label><p>{selected.agreement_type === 'sale' ? 'Sale Agreement' : 'Lease Cum Sale Agreement'}</p></div>
              <div><label>Created At</label><p>{formatDate(selected.created_at)}</p></div>
              {selected.created_by && <div><label>Created By</label><p>{selected.created_by}</p></div>}
            </div>
          </div>

          <div className="detail-section">
            <h3>Buyer Information {buyers.length > 1 ? `(${buyers.length} Buyers)` : ''}</h3>
            {buyers.map((buyer, index) => (
              <div key={index} className={buyers.length > 1 ? 'buyer-detail-block' : ''}>
                {buyers.length > 1 && <p className="buyer-detail-label">Buyer {index + 1}</p>}
                <div className="detail-grid">
                  <div><label>Company</label><p>{buyer.buyerCompany || '—'}</p></div>
                  {index === 0 && <div><label>License Number</label><p>{buyer.licenseNumber || '—'}</p></div>}
                  <div><label>Representative</label><p>{buyer.representativeName || '—'}</p></div>
                  <div><label>EID / Passport</label><p>{buyer.eid || '—'}</p></div>
                  <div><label>Mobile</label><p>{buyer.mobile || '—'}</p></div>
                </div>
                {(buyer.idFrontUrl || buyer.idBackUrl) && (
                  <div className="buyer-id-image">
                    <label>Customer ID</label>
                    <div className="admin-id-sides">
                      {buyer.idFrontUrl && <div><p>Front</p><img src={buyer.idFrontUrl} alt="ID Front" /></div>}
                      {buyer.idBackUrl && <div><p>Back</p><img src={buyer.idBackUrl} alt="ID Back" /></div>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {(selected.guarantor_name || selected.guarantor_eid) && (
            <div className="detail-section">
              <h3>Guarantor Information</h3>
              <div className="detail-grid">
                <div><label>Name</label><p>{selected.guarantor_name || '—'}</p></div>
                <div><label>CNIC / EID</label><p>{selected.guarantor_eid || '—'}</p></div>
                <div><label>Mobile</label><p>{selected.guarantor_mobile || '—'}</p></div>
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Products</h3>
            <table className="admin-table">
              <thead><tr><th>SR</th><th>Product</th><th>Qty</th><th>Unit</th><th>Rate ({detailCur})</th><th>Amount ({detailCur})</th></tr></thead>
              <tbody>
                {selected.products.map((p, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td><td>{p.name}</td><td>{p.qty}</td><td>{p.unit}</td>
                    <td>{parseFloat(p.rate).toFixed(2)}</td>
                    <td>{(parseFloat(p.qty) * parseFloat(p.rate)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="detail-section">
            <h3>Payment Summary</h3>
            <div className="detail-grid">
              <div><label>Subtotal</label><p>{detailCur} {parseFloat(selected.subtotal).toFixed(2)}</p></div>
              <div><label>VAT (5%)</label><p>{detailCur} {parseFloat(selected.vat).toFixed(2)}</p></div>
              <div><label>Total</label><p>{detailCur} {parseFloat(selected.total).toFixed(2)}</p></div>
              <div><label>Token Amount</label><p>{detailCur} {parseFloat(selected.token_amount).toFixed(2)}</p></div>
              <div><label>Delivery Amount</label><p>{detailCur} {parseFloat(selected.delivery_amount).toFixed(2)}</p></div>
              <div><label>Remaining Balance</label><p>{detailCur} {parseFloat(selected.remaining_balance).toFixed(2)}</p></div>
              <div><label>Installments</label><p>{selected.installment_months} months</p></div>
              <div><label>Monthly Installment</label><p>{detailCur} {parseFloat(selected.monthly_installment).toFixed(2)}</p></div>
            </div>
          </div>

          {selected.payment_schedule && selected.payment_schedule.length > 0 && (
            <div className="detail-section">
              <h3>Payment Schedule</h3>
              <table className="admin-table">
                <thead><tr><th>Month</th><th>Period</th><th>Due Date</th><th>Amount ({detailCur})</th></tr></thead>
                <tbody>
                  {selected.payment_schedule.map((row) => (
                    <tr key={row.month}><td>{row.month}</td><td>{row.label}</td><td>{row.dueDate}</td><td>{row.amount}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selected.extra_payment_terms && selected.extra_payment_terms.length > 0 && (
            <div className="detail-section">
              <h3>Additional Payment Terms</h3>
              {selected.extra_payment_terms.map((term, i) => (
                <p key={i} className="detail-term"><strong>{['V','VI','VII','VIII','IX','X'][i]}-</strong> {term}</p>
              ))}
            </div>
          )}

          {selected.extra_standard_terms && selected.extra_standard_terms.length > 0 && (
            <div className="detail-section">
              <h3>Additional Standard Terms</h3>
              {selected.extra_standard_terms.map((term, i) => (
                <p key={i} className="detail-term"><strong>{String(i + 7).padStart(2, '0')}-</strong> {term}</p>
              ))}
            </div>
          )}

          {selected.remote_signatures && Object.keys(selected.remote_signatures).length > 0 && (
            <div className="detail-section">
              <h3>Signatures</h3>
              <div className="admin-id-sides">
                {Object.entries(selected.remote_signatures).map(([index, dataUrl]) => (
                  <div key={index}>
                    <p>{buyers[parseInt(index)]?.representativeName || `Buyer ${parseInt(index) + 1}`}</p>
                    <img src={dataUrl} alt={`Buyer ${parseInt(index) + 1} remote signature`}
                      style={{ width: '180px', height: '90px', objectFit: 'contain', border: '1px solid var(--grey-mid)', borderRadius: '6px', background: 'white' }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected.remote_photos && Object.keys(selected.remote_photos).length > 0 && (
            <div className="detail-section">
              <h3>Verification Photos</h3>
              <div className="admin-id-sides">
                {Object.entries(selected.remote_photos).map(([index, dataUrl]) => (
                  <div key={index}>
                    <p>{buyers[parseInt(index)]?.representativeName || `Buyer ${parseInt(index) + 1}`}</p>
                    <img src={dataUrl} alt={`Buyer ${parseInt(index) + 1} verification`}
                      style={{ width: '180px', height: '130px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--grey-mid)' }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── EMPLOYEE FORM ──
  if (empView === 'add' || empView === 'edit') {
    return (
<div className="page emp-form-page">
        <div className="emp-form-header">
  <button className="btn-back-admin" onClick={() => { setEmpView('list'); setEmpError(''); setEmpForm({ name: '', username: '', password: '', role: 'viewer' }); }}>
    ← Back to Employees
  </button>
  <h1>{empView === 'add' ? 'Add Employee' : 'Edit Employee'}</h1>
  <div></div>
</div>

<div className="emp-form-card">
          <div className="emp-form-section">
            {empError && <p className="emp-form-error">⚠️ {empError}</p>}

            <div className="emp-form-group">
              <label>Full Name *</label>
              <input type="text" placeholder="e.g. Ahmad Mohsin" value={empForm.name}
                onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} />
            </div>
            <div className="emp-form-group">
              <label>Username *</label>
              <input type="text" placeholder="e.g. ahmad.mohsin" value={empForm.username}
                onChange={(e) => setEmpForm({ ...empForm, username: e.target.value })} />
            </div>
            <div className="emp-form-group">
              <label>Password *</label>
              <input type="password" placeholder="Set a password" value={empForm.password}
                onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} />
            </div>

            <div className="emp-form-group">
              <label>Role *</label>
              <div className="role-selector">
                {ROLES.map(role => (
                  <div
                    key={role.key}
                    className={`role-option ${empForm.role === role.key ? 'selected' : ''}`}
                    onClick={() => setEmpForm({ ...empForm, role: role.key })}
                    style={{ '--role-color': role.color }}
                  >
                    <div className="role-option-top">
                      <span className="role-option-label">{role.label}</span>
                      {empForm.role === role.key && <span className="role-option-check">✓</span>}
                    </div>
                    <p className="role-option-desc">{role.description}</p>
                    <div className="role-permissions">
                      {role.permissions.map(p => (
                        <span key={p} className="role-perm-tag">{p.replace('_', ' ')}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="emp-form-actions">
              <button className="btn-emp-cancel" onClick={() => { setEmpView('list'); setEmpError(''); }}>Cancel</button>
              <button className="btn-emp-save" onClick={handleSaveEmployee} disabled={empSaving}>
                {empSaving ? 'Saving...' : empView === 'add' ? '+ Add Employee' : '✓ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── CUSTOMER DETAIL ──
  if (selectedCust) {
    const isPakC = selectedCust.customer_type === 'pakistan';
    return (
      <div className="page">
        <div className="admin-detail-header">
          <button className="btn-back-admin" onClick={() => setSelectedCust(null)}>← Back to Customers</button>
          <span className={`admin-cust-badge ${isPakC ? 'pak' : 'uae'}`} style={{ fontSize: '14px', padding: '4px 12px' }}>
            {isPakC ? '🇵🇰 Pakistan' : '🇦🇪 UAE'}
          </span>
        </div>
        <div className="detail-card">

          <div className="detail-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: isPakC ? '#2d6a4f' : '#c8a96e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '700', color: 'white', flexShrink: 0 }}>
                {selectedCust.business_name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 style={{ margin: 0 }}>{selectedCust.business_name}</h3>
                <span style={{ fontSize: '12px', color: '#999' }}>{selectedCust.customer_id} · {selectedCust.legal_entity || 'No entity type'}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Legal Information</h3>
            <div className="detail-grid">
              <div><label>Business Name</label><p>{selectedCust.business_name || '—'}</p></div>
              <div><label>Legal Entity</label><p>{selectedCust.legal_entity || '—'}</p></div>
              {isPakC ? (
                <>
                  <div><label>NTN</label><p>{selectedCust.ntn || '—'}</p></div>
                  <div><label>GST</label><p>{selectedCust.gst || '—'}</p></div>
                </>
              ) : (
                <>
                  <div><label>License Number</label><p>{selectedCust.license_number || '—'}</p></div>
                  <div><label>VAT Number</label><p>{selectedCust.vat || '—'}</p></div>
                </>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>Personal Information</h3>
            <div className="detail-grid">
              <div><label>Representative Name</label><p>{selectedCust.representative_name || '—'}</p></div>
              <div><label>Contact Number 1</label><p>{selectedCust.contact_number_1 || '—'}</p></div>
              {selectedCust.contact_number_2 && <div><label>Contact Number 2</label><p>{selectedCust.contact_number_2}</p></div>}
              <div><label>Email</label><p>{selectedCust.email || '—'}</p></div>
              {!isPakC && <div><label>EID / Passport</label><p>{selectedCust.eid_passport || '—'}</p></div>}
            </div>
          </div>

          <div className="detail-section">
            <h3>Address</h3>
            <div className="detail-grid">
              <div><label>Region</label><p>{selectedCust.region || '—'}</p></div>
              <div><label>Territory / City</label><p>{selectedCust.territory || '—'}</p></div>
              <div><label>Near Landmark</label><p>{selectedCust.landmark || '—'}</p></div>
              <div><label>Office Phone</label><p>{selectedCust.address_phone || '—'}</p></div>
              <div><label>Office Email</label><p>{selectedCust.address_email || '—'}</p></div>
              <div><label>Website</label><p>{selectedCust.website || '—'}</p></div>
              <div><label>Customer Since</label><p>{formatDate(selectedCust.created_at)}</p></div>
            </div>
          </div>

          {(selectedCust.id_front_url || selectedCust.id_back_url) && (
            <div className="detail-section">
              <h3>{isPakC ? 'CNIC Images' : 'Emirates ID / Passport Images'}</h3>
              <div className="admin-id-sides">
                {selectedCust.id_front_url && (
                  <div>
                    <p>Front</p>
                    <img src={selectedCust.id_front_url} alt="ID Front" style={{ maxWidth: '260px', borderRadius: '8px', border: '1px solid var(--grey-mid)' }} />
                  </div>
                )}
                {selectedCust.id_back_url && (
                  <div>
                    <p>Back</p>
                    <img src={selectedCust.id_back_url} alt="ID Back" style={{ maxWidth: '260px', borderRadius: '8px', border: '1px solid var(--grey-mid)' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {(selectedCust.payment_method || selectedCust.bank_name || selectedCust.account_number) && (
            <div className="detail-section">
              <h3>Financial Information</h3>
              <div className="detail-grid">
                {selectedCust.payment_method && <div><label>Payment Method</label><p>{selectedCust.payment_method}</p></div>}
                {selectedCust.payment_terms && <div><label>Payment Terms</label><p>{selectedCust.payment_terms}</p></div>}
                {selectedCust.bank_name && <div><label>Bank Name</label><p>{selectedCust.bank_name}</p></div>}
                {selectedCust.account_number && <div><label>Account Number</label><p>{selectedCust.account_number}</p></div>}
              </div>
            </div>
          )}

          {selectedCust.partners && selectedCust.partners.length > 0 && (
            <div className="detail-section">
              <h3>Partners ({selectedCust.partners.length})</h3>
              {selectedCust.partners.map((partner, i) => (
                <div key={i} className="buyer-detail-block">
                  <p className="buyer-detail-label">Partner {i + 1}</p>
                  <div className="detail-grid">
                    <div><label>Name</label><p>{partner.name || '—'}</p></div>
                    <div><label>{isPakC ? 'CNIC' : 'EID / Passport'}</label><p>{partner.id_number || '—'}</p></div>
                  </div>
                  {(partner.id_front_url || partner.id_back_url) && (
                    <div className="admin-id-sides" style={{ marginTop: '10px' }}>
                      {partner.id_front_url && (
                        <div>
                          <p>ID Front</p>
                          <img src={partner.id_front_url} alt="Partner ID Front" style={{ maxWidth: '220px', borderRadius: '8px', border: '1px solid var(--grey-mid)' }} />
                        </div>
                      )}
                      {partner.id_back_url && (
                        <div>
                          <p>ID Back</p>
                          <img src={partner.id_back_url} alt="Partner ID Back" style={{ maxWidth: '220px', borderRadius: '8px', border: '1px solid var(--grey-mid)' }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    );
  }

  // ── MAIN DASHBOARD ──
  return (
    <div className="page">
      {ToastComponent}
      {ConfirmComponent}
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <button className="btn-logout" onClick={() => { logout(); navigate('/hub'); }}>Logout</button>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <h3>{agreements.length}</h3>
          <p>Total Agreements</p>
        </div>
        <div className="stat-card">
          <h3>{agreements.filter(a => a.agreement_type === 'sale').length}</h3>
          <p>Sale Agreements</p>
        </div>
        <div className="stat-card">
          <h3>{agreements.filter(a => a.agreement_type === 'lease').length}</h3>
          <p>Lease Agreements</p>
        </div>
        <div className="stat-card">
          <h3>AED {agreements.reduce((sum, a) => sum + parseFloat(a.total || 0), 0).toLocaleString('en', { minimumFractionDigits: 2 })}</h3>
          <p>Total Value</p>
        </div>
        <div className="stat-card">
          <h3>{employees.filter(e => e.is_active).length}</h3>
          <p>Active Employees</p>
        </div>
        <div className="stat-card">
          <h3>{customers.length}</h3>
          <p>Total Customers</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={activeTab === 'agreements' ? 'active' : ''} onClick={() => setActiveTab('agreements')}>
          📄 Agreements
        </button>
        <button className={activeTab === 'employees' ? 'active' : ''} onClick={() => setActiveTab('employees')}>
          👥 Employees
        </button>
        <button className={activeTab === 'customers' ? 'active' : ''} onClick={() => setActiveTab('customers')}>
          🏢 Customers
        </button>
      </div>

      {/* ── AGREEMENTS TAB ── */}
      {activeTab === 'agreements' && (
        <>
          <div className="admin-filters">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
            <button className={filter === 'sale' ? 'active' : ''} onClick={() => setFilter('sale')}>Sale</button>
            <button className={filter === 'lease' ? 'active' : ''} onClick={() => setFilter('lease')}>Lease</button>
          </div>

          {loading ? (
            <p className="loading-text">Loading agreements...</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ref Number</th>
                  <th>Type</th>
                  <th>Buyer(s)</th>
                  <th>Date</th>
                  <th>Total (AED)</th>
                  <th>Signed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id}>
                    <td>{a.ref_number}</td>
                    <td><span className={`badge ${a.agreement_type}`}>{a.agreement_type === 'sale' ? 'Sale' : 'Lease'}</span></td>
                    <td>
                      {a.buyers && a.buyers.length > 1
                        ? <span>{a.buyer_company} <span className="buyers-count">+{a.buyers.length - 1} more</span></span>
                        : a.buyer_company}
                    </td>
                    <td>{a.date}</td>
                    <td>{parseFloat(a.total).toFixed(2)}</td>
                    <td>
                      {a.remote_signatures && Object.keys(a.remote_signatures).length > 0
                        ? <span className="signed-badge">✓ Signed</span>
                        : <span className="pending-badge">Pending</span>}
                    </td>
                    <td><button className="btn-view" onClick={() => setSelected(a)}>View</button></td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="7" style={{ textAlign: 'center', color: '#999' }}>No agreements found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ── EMPLOYEES TAB ── */}
      {activeTab === 'employees' && (
        <>
          <div className="emp-tab-header">
            <p className="emp-tab-subtitle">{employees.length} employees · {employees.filter(e => e.is_active).length} active</p>
            <button className="btn-add-emp" onClick={() => { setEmpView('add'); setEmpForm({ name: '', username: '', password: '', role: 'viewer' }); setEmpError(''); }}>
              + Add Employee
            </button>
          </div>

          {/* Role Legend */}
          <div className="role-legend">
            {ROLES.map(role => (
              <div key={role.key} className="role-legend-item" style={{ '--role-color': role.color }}>
                <span className="role-legend-dot"></span>
                <span className="role-legend-label">{role.label}</span>
                <span className="role-legend-desc">— {role.description}</span>
              </div>
            ))}
          </div>

          {loadingEmployees ? (
            <p className="loading-text">Loading employees...</p>
          ) : employees.length === 0 ? (
            <div className="emp-empty">
              <p>No employees yet. Add your first employee!</p>
            </div>
          ) : (
            <div className="emp-list">
              {employees.map((emp) => {
                const role = getRoleInfo(emp.role);
                return (
                  <div key={emp.id} className={`emp-card ${!emp.is_active ? 'inactive' : ''}`}>
                    <div className="emp-card-left">
                      <div className="emp-avatar" style={{ background: role.color }}>
                        {emp.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="emp-name-row">
                          <p className="emp-name">{emp.name}</p>
                          {!emp.is_active && <span className="emp-inactive-tag">Inactive</span>}
                        </div>
                        <p className="emp-username">@{emp.username}</p>
                        <div className="emp-role-badge" style={{ background: role.color + '22', color: role.color, border: `1px solid ${role.color}` }}>
                          {role.label}
                        </div>
                      </div>
                    </div>

                    <div className="emp-permissions">
                      {role.permissions.map(p => (
                        <span key={p} className="emp-perm-chip">{p.replace('_', ' ')}</span>
                      ))}
                    </div>

                    <div className="emp-card-actions">
                      <button className="btn-emp-edit" onClick={() => handleEditEmployee(emp)}>✏️ Edit</button>
                      <button
                        className={`btn-emp-toggle ${emp.is_active ? 'deactivate' : 'activate'}`}
                        onClick={() => handleToggleActive(emp)}
                      >
                        {emp.is_active ? '⏸ Deactivate' : '▶ Activate'}
                      </button>
                      <button className="btn-emp-delete" onClick={() => handleDeleteEmployee(emp)}>🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── CUSTOMERS TAB ── */}
      {activeTab === 'customers' && (
        <>
          <div className="emp-tab-header">
            <p className="emp-tab-subtitle">
              {customers.length} total · {customers.filter(c => c.customer_type === 'pakistan').length} Pakistan · {customers.filter(c => c.customer_type === 'uae').length} UAE
            </p>
          </div>

          <div className="admin-cust-filters">
            <button className={custTypeFilter === 'all' ? 'active' : ''} onClick={() => setCustTypeFilter('all')}>All</button>
            <button className={custTypeFilter === 'pakistan' ? 'active' : ''} onClick={() => setCustTypeFilter('pakistan')}>🇵🇰 Pakistan</button>
            <button className={custTypeFilter === 'uae' ? 'active' : ''} onClick={() => setCustTypeFilter('uae')}>🇦🇪 UAE</button>
          </div>

          <div className="admin-cust-search">
            <input
              type="text"
              placeholder="Search by name, representative or ID..."
              value={custSearch}
              onChange={(e) => setCustSearch(e.target.value)}
            />
            {custSearch && <button onClick={() => setCustSearch('')}>✕</button>}
          </div>

          {loadingCustomers ? (
            <p className="loading-text">Loading customers...</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Customer ID</th>
                  <th>Region</th>
                  <th>Business Name</th>
                  <th>Representative</th>
                  <th>Contact</th>
                  <th>Legal Entity</th>
                  <th>Added</th>
                  <th>Action</th>
                </tr>
              </thead>
              {(() => {
                const filteredCustomers = customers.filter(c => {
                  const matchType = custTypeFilter === 'all' || c.customer_type === custTypeFilter;
                  const matchSearch = !custSearch ||
                    c.business_name?.toLowerCase().includes(custSearch.toLowerCase()) ||
                    c.representative_name?.toLowerCase().includes(custSearch.toLowerCase()) ||
                    c.customer_id?.toLowerCase().includes(custSearch.toLowerCase());
                  return matchType && matchSearch;
                });
                return (
                  <tbody>
                    {filteredCustomers.map((c) => (
                      <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedCust(c)}>
                        <td><span className="customer-card-id">{c.customer_id}</span></td>
                        <td>
                          <span className={`admin-cust-badge ${c.customer_type === 'pakistan' ? 'pak' : 'uae'}`}>
                            {c.customer_type === 'pakistan' ? '🇵🇰 PAK' : '🇦🇪 UAE'}
                          </span>
                        </td>
                        <td>{c.business_name}</td>
                        <td>{c.representative_name || '—'}</td>
                        <td>{c.contact_number_1 || '—'}</td>
                        <td>{c.legal_entity || '—'}</td>
                        <td>{formatDate(c.created_at)}</td>
                        <td><button className="btn-view" onClick={(e) => { e.stopPropagation(); setSelectedCust(c); }}>View</button></td>
                      </tr>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <tr><td colSpan="8" style={{ textAlign: 'center', color: '#999' }}>No customers found.</td></tr>
                    )}
                  </tbody>
                );
              })()}
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default Admin;